import { t } from "@lingui/core/macro";
import { captureException } from "@sentry/react-native";
import { useAtom } from "jotai";
import { useCallback } from "react";
import { GraphQLTaggedNode, useMutation, VariablesOf } from "react-relay";
import type { UseMutationConfig } from "react-relay/relay-hooks/useMutation";
import {
  CacheConfig,
  MutationParameters,
  RecordSourceSelectorProxy,
  PayloadError,
} from "relay-runtime";

import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { Text } from "@whoosh/design-system/src/components/primitive";

import { OverlayLoading_atom } from "../../features/OverlayLoader/atoms";

export type WhooshMutationError = {
  code: string;
  field: string[];
  bookingIndex?: number | null;
  message?: string | null;
};

export const TextError = ({ error }: { error: string }) => (
  <Text
    maxFontSizeMultiplier={2}
    maxWidth="100%"
    testID="use-mutation-with-error-text"
  >
    {error}
  </Text>
);

export const getMutationErrorMessage = (error?: WhooshMutationError | null) => {
  if (error?.message) {
    return error.message;
  }

  return t`An unknown error occurred. Please try again.`;
};

type doMutationProps<TMutation extends MutationParameters> = {
  variables: VariablesOf<TMutation>;
  cacheConfig?: CacheConfig;
  onCompleted: (res: TMutation["response"]) => void;
  onCompletedErrorCustom?: (
    errors: WhooshMutationError[],
    response: TMutation["response"]
  ) => WhooshMutationError[];
  onErrorCustom?: (error: Error) => void;
  onFailure?: () => void;
  optimisticResponse?: TMutation["rawResponse"];
  optimisticUpdater?: (
    store: RecordSourceSelectorProxy<TMutation["response"]>
  ) => void;
  updater?: (store: RecordSourceSelectorProxy<TMutation["response"]>) => void;
  onPayloadError?: (errors: PayloadError[]) => void;
};

export const useMutationWithError = <TMutation extends MutationParameters>(
  mutationString: GraphQLTaggedNode
) => {
  const { showToast } = useToast();
  const [, setLoading] = useAtom(OverlayLoading_atom);
  const [runMutation, isLoading] = useMutation(mutationString);

  const doMutation = useCallback(
    ({
      variables,
      cacheConfig,
      onCompleted,
      onCompletedErrorCustom,
      onErrorCustom,
      onFailure,
      optimisticResponse,
      optimisticUpdater,
      updater,
      onPayloadError,
    }: doMutationProps<TMutation>) => {
      const mutationConfig: UseMutationConfig<MutationParameters> & {
        cacheConfig?: CacheConfig;
      } = {
        variables,
        cacheConfig,
        optimisticResponse,
        optimisticUpdater: (store) => {
          !!optimisticUpdater && optimisticUpdater(store);
        },
        updater,
        onCompleted: (res: any, errors): void | null => {
          if (errors) {
            onFailure?.();
            setLoading(false);
            if (onPayloadError) {
              onPayloadError(errors);
            } else {
              showToast({
                description: errors[0].message,
                semanticStyle: "error",
                toastId: "use-mutation-with-error-toast",
              });
              errors.forEach((e) => {
                captureException(e);
              });
            }
          } else if (!!res.responsePayload?.success) {
            onCompleted(res);
          } else {
            onFailure?.();
            if (onCompletedErrorCustom) {
              const remainingErrors = onCompletedErrorCustom(
                res.responsePayload.errors,
                res
              );
              if (remainingErrors && remainingErrors.length > 0) {
                captureException(remainingErrors);
                remainingErrors.forEach((error, index) => {
                  if (index === 0) {
                    showToast({
                      description: getMutationErrorMessage(error),
                      semanticStyle: "error",
                      toastId: "use-mutation-with-error-toast",
                    });
                  }
                });
              }
            } else {
              // We always report unhandled GQL level errors up to Sentry.
              if (res.responsePayload.errors.length > 0) {
                captureException(res.responsePayload.errors);
              }

              showToast({
                description: getMutationErrorMessage(
                  res.responsePayload.errors[0]
                ),
                semanticStyle: "error",
                toastId: "use-mutation-with-error-toast",
              });
            }
            setLoading(false);
          }
        },
        onError: (error) => {
          onFailure?.();
          !!onErrorCustom && onErrorCustom(error);
          captureException(error);
          showToast({
            description: error.message,
            semanticStyle: "error",
            toastId: "use-mutation-with-error-toast",
          });
          setLoading(false);
        },
      };
      runMutation(mutationConfig);
    },
    [runMutation, setLoading, showToast]
  );

  return [doMutation, isLoading] as const;
};
